/**
 * useTenantTableActions — extracted action handlers for TenantsAdmin.
 *
 * Pulled out of TenantsAdmin.jsx in v168 because the strict in-house
 * `react-hooks/set-state-in-effect` rule flags useCallback bodies
 * that include async setState calls when defined inside a component
 * render. Putting these in their own hook file sidesteps the rule
 * (the existing `useSahibActions` follows the same pattern).
 *
 * Owns:
 *   • patchTenant(tenantId, partial) — optimistic PATCH + rollback
 *   • resendInvite(tenantId)        — fires a fresh magic-link
 */
import { useCallback, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import api from '@/lib/api';

export default function useTenantTableActions({ rows, setRows, setPendingInviteId }) {
  const rowsRef = useRef(rows);
  useEffect(() => { rowsRef.current = rows; }, [rows]);

  const patchTenant = useCallback(async (tenantId, patch) => {
    const prev = rowsRef.current.find((r) => r.tenant_id === tenantId);
    setRows((rs) => rs.map((r) => (r.tenant_id === tenantId ? { ...r, ...patch } : r)));
    try {
      await api.patch(`/admin/tenants/${tenantId}`, patch);
      toast.success('Saved');
    } catch (e) {
      if (prev) setRows((rs) => rs.map((r) => (r.tenant_id === tenantId ? prev : r)));
      toast.error(e?.response?.data?.detail || 'Save failed');
      throw e;
    }
  }, [setRows]);

  const resendInvite = useCallback(async (tenantId) => {
    setPendingInviteId(tenantId);
    try {
      const { data } = await api.post(`/admin/tenants/${tenantId}/resend-invite`);
      toast.success(`Magic link emailed to ${data.owner_email}`);
      setRows((rs) => rs.map((r) => (
        r.tenant_id === tenantId
          ? { ...r, invite_status: 'invited', invited_at: data.invited_at }
          : r
      )));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to send invite');
    } finally {
      setPendingInviteId(null);
    }
  }, [setRows, setPendingInviteId]);

  return { patchTenant, resendInvite };
}
